// TRAERSE LA CARTERA ENTERA, NO LA PRIMERA PAGINA.
//
// ── LO QUE ROMPIA ──────────────────────────────────────────────────────────
//
// El servidor manda los clientes por paginas de 500 como mucho
// (`paginationDefaults` los recorta ahi). Al entrar, la aplicacion pide todas
// las paginas y las pega: una agencia de 955 clientes carga 500 + 455.
//
// Pero habia DOS sitios que volvian a pedir la lista despues de tocarla —al
// restaurar un cliente de la papelera y al combinar duplicados— y los dos
// pedian SOLO la pagina 1 y con eso reemplazaban el libro entero:
//
//   listClients({ page: 1, limit: 500 }).then(({ clients }) => onClientsChange(clients))
//
// De 955 clientes quedaban 500. Los otros 455 desaparecian de la lista, de los
// contadores de las pestañas, de los numeros de los filtros, de los correos
// masivos y de todo lo que lee la lista en memoria — sin un aviso, y hasta que
// alguien recargara la pagina. Y lo hacian justo despues de "combinar
// duplicados", que es lo que aprieta quien esta investigando por que no le
// cuadran los numeros (Wendy Cercio, 2026-09-17).
//
// Es la regla de oro 118 otra vez: el arranque ya sabia recorrer las paginas,
// estos dos caminos no. Asi que el recorrido se escribe UNA vez y se usa en los
// tres sitios.
//
// ── POR QUE NO LO USA TAMBIEN EL BOTON DE HOJAS ────────────────────────────
//
// `refreshSheetsData` en deskte-full.jsx se trae los DOS libros —clientes y
// polizas— y los pasa por UN SOLO limitador a proposito: con uno por libro
// serian 3 + 3 peticiones pesadas encima del servidor a media jornada. Partirlo
// para que los clientes salieran por aqui rompe exactamente eso. Se queda como
// esta, y esta nota existe para que se sepa que no es un olvido.

import { enTandas, ANCHO_POR_DEFECTO } from './enTandas.js';

/** Cuantos clientes caben en una pagina. El servidor no da mas de 500. */
export const CLIENTES_POR_PAGINA = 500;

/**
 * El tope de paginas. 100 x 500 = 50.000 clientes.
 *
 * No es una preferencia: es el freno para que un servidor que conteste mal
 * —una `totalPages` absurda— no deje al navegador pidiendo paginas para
 * siempre. Se subio de 20 cuando entraron agencias de mas de 1.000.
 */
export const TOPE_DE_PAGINAS = 100;

/**
 * Trae TODOS los clientes, pagina a pagina y en tandas.
 *
 * @param {object} opciones
 * @param {(p: {page:number, limit:number}) => Promise<{clients:any[], pagination:any}>} opciones.listClients
 *   la funcion que pide una pagina (`api.js#listClients`). Se pasa de fuera
 *   para que este archivo no arrastre el cliente de API entero ni el token.
 * @param {number} [opciones.ancho] cuantas paginas a la vez.
 * @returns {Promise<{clientes:any[], paginasFallidas:number, totalDelServidor:number|null}>}
 *
 * `paginasFallidas` NO se traga: quien llama decide que hacer con una lista a
 * medias, y las dos respuestas razonables son distintas. Al arrancar no hay
 * lista anterior, asi que media lista es mejor que ninguna y se avisa; al
 * refrescar SI la hay, y reemplazar una completa por una incompleta es
 * justamente el fallo que este archivo viene a arreglar.
 *
 * Si la pagina 1 falla se propaga la excepcion: sin ella no se sabe ni cuantas
 * paginas hay, y devolver `{ clientes: [] }` se leeria como "esta agencia no
 * tiene clientes".
 */
export async function cargarCarteraCompleta({ listClients, ancho = ANCHO_POR_DEFECTO }) {
  const primera = await listClients({ page: 1, limit: CLIENTES_POR_PAGINA });
  const clientes = [...(primera.clients || [])];
  const totalDelServidor = primera.pagination && typeof primera.pagination.total === 'number'
    ? primera.pagination.total
    : null;

  const paginas = Math.min(TOPE_DE_PAGINAS, (primera.pagination && primera.pagination.totalPages) || 1);
  let paginasFallidas = 0;
  if (paginas > 1) {
    // `enTandas` devuelve EN ORDEN, asi que las paginas se pegan como venian y
    // el listado no sale barajado.
    const resto = await enTandas(
      Array.from({ length: paginas - 1 }, (_, i) => () =>
        listClients({ page: i + 2, limit: CLIENTES_POR_PAGINA })
          .then((r) => r.clients || [])
          // Una pagina caida no se descarta en silencio: se cuenta y quien
          // llama lo dice. Descartarla sin mas deja un libro incompleto con
          // buena cara, que es lo que no se puede detectar desde fuera.
          .catch(() => { paginasFallidas += 1; return []; }),
      ),
      ancho,
    );
    for (const filas of resto) clientes.push(...filas);
  }

  return { clientes, paginasFallidas, totalDelServidor };
}
